import { useEffect, useRef, useState } from "react";
import {
  receiveMessage,
  STEALTH_SESSION_EVENT,
} from "@nextlayer/stealth-shared";
import { LoginForm } from "./components/LoginForm";
import { InvoiceConfirm } from "./components/InvoiceConfirm";
import { StealthLogo } from "./components/StealthLogo";
import { emitToSdk } from "./messaging";
import { getSdkOrigin } from "./config";
import { clearSession, loadSession, saveSession } from "./session";

type View = "login" | "invoice";

export function App() {
  const [view, setView] = useState<View>("login");
  const [clientId, setClientId] = useState<string | null>(null);
  // Set once the user explicitly signs out. window.nlStealth still reports the
  // extension session as authenticated, so without this a later re-announce would
  // bounce the user straight back to the invoice view. Sign-out wins over auto-identify.
  const signedOut = useRef(false);

  // On load: prefer the extension's identity, then a persisted session, else login.
  useEffect(() => {
    const identify = (): void => {
      if (signedOut.current) return;
      const stealth = window.nlStealth;
      if (stealth?.isAuthenticated && stealth.clientId) {
        setClientId(stealth.clientId);
        setView("invoice");
        return;
      }
      const session = loadSession();
      if (session) {
        setClientId(session.clientId);
        setView("invoice");
      }
    };

    identify();

    window.addEventListener(STEALTH_SESSION_EVENT, identify);
    return () => window.removeEventListener(STEALTH_SESSION_EVENT, identify);
  }, []);

  // Inbound SDK messages — trust only the exact embedding (parent) origin.
  useEffect(
    () =>
      receiveMessage(
        (message) => {
          if (message.type === "CLOSE") emitToSdk("CLOSE");
        },
        [getSdkOrigin()],
      ),
    [],
  );

  const handleAuthSuccess = (id: string) => {
    signedOut.current = false;
    setClientId(id);
    saveSession(id);
    emitToSdk("AUTH_SUCCESS", { clientId: id });
    setView("invoice");
  };

  const handleSignOut = () => {
    signedOut.current = true;
    clearSession();
    setClientId(null);
    setView("login");
  };

  return (
    <main className="nl-modal">
      <header className="nl-modal__header">
        <StealthLogo />
      </header>

      {view === "login" ? (
        <LoginForm onSuccess={handleAuthSuccess} />
      ) : (
        <>
          <InvoiceConfirm clientId={clientId} />
          <button className="nl-link" type="button" onClick={handleSignOut}>
            Not you? Sign out
          </button>
        </>
      )}
    </main>
  );
}
